// The v2 practical-lesson script: turns a route's segment list into laid-out world
// positions and a driving-order list of Tasks the child has to complete. Pure — no Phaser,
// no config imports. `segmentFeatures.mjs` supplies the per-segment sign/line/bay
// placeholders this reads; the scene that eventually drives the car (Task 7) is the only
// thing that imports Phaser.

import { SEGMENT_WORLD_WIDTH, ROUTE_FINISH_X } from '../ui/carCatalog.mjs';
import { featureWorldX } from './segmentFeatures.mjs';

// Mirrors backend app/game/catalog.py — every segment id a route may reference.
export const SEGMENT_IDS = [
  'country', 'limit50', 'stop', 'yield', 'crossing', 'lights', 'solid', 'school', 'noentry', 'parking',
];

// Mirrors the backend catalog's TRAINING_ROUTES/EXAM_ROUTE segment lists (catalog.py). The
// scene prefers the server's own `catalog.driving.routes[n].segments` — these are the
// fallback used before the catalog has loaded, and by every pure test in this file.
export const ROUTE_DEFS = {
  lesson1: ['country', 'limit50', 'stop', 'country'],
  lesson2: ['limit50', 'crossing', 'lights', 'school', 'noentry'],
  lesson3: ['solid', 'country', 'yield', 'country', 'parking'],
  exam: ['limit50', 'stop', 'crossing', 'lights', 'solid', 'country', 'yield', 'school', 'parking'],
};

/**
 * Every `[a, b]` where segment `a` is directly followed by `b` on some route (driving order,
 * so `b` lies to the LEFT of `a`), first-seen order — one junction patch each, see
 * scripts/compose_junctions.py. `junctionKey(a, b)` is the texture the patch is loaded as.
 */
export function junctionPairs(routes = ROUTE_DEFS) {
  const seen = [];
  for (const segs of Object.values(routes)) {
    segs.slice(0, -1).forEach((a, i) => {
      const b = segs[i + 1];
      if (!seen.some(([x, y]) => x === a && y === b)) seen.push([a, b]);
    });
  }
  return seen;
}

export const JUNCTION_PAIRS = junctionPairs();
export const junctionKey = (a, b) => `junction_${a}__${b}`;
// A patch strip is 1671 source px wide (two 835/836-px edges) at the segments' 941-px
// height, drawn at the same scale as the segments: 1671 × (SEGMENT_WORLD_WIDTH / 3342).
export const JUNCTION_WORLD_WIDTH = 1278.5;

// Mirrors backend app/game/catalog.py's TASK_SEVERITY — the server is the one that actually
// scores a run, but the client needs the same table to show a task's weight up front.
const SEVERITY_BY_KIND = {
  limit: 'minor', lane: 'minor',
  stop: 'major', yield: 'major', crossing: 'major', light: 'major', solid: 'major',
  crash: 'major', oncoming: 'major',
  noentry: 'none', park: 'major', finish: 'none', overtake: 'none',
};

// Copy for every task kind buildTasks() can emit, plus `crash` (raised at runtime on a
// collision, not by buildTasks itself). `title` is always shown; `hint` only in lesson 1
// (see buildTasks) — both may be a plain string or a function of the task's own `meta`.
export const TASK_COPY_PL = {
  stop: {
    icon: '{sign_stop}', title: 'Zatrzymaj się przed znakiem STOP',
    hint: 'Hamuj wcześniej i stań całkiem przed linią. Poczekaj, aż auto z boku przejedzie.',
    ok: 'Super, pełne zatrzymanie!', fail: 'Ojej — trzeba było się zatrzymać przed linią',
  },
  limit: {
    icon: '{sign_speed}', title: (meta) => `Nie szybciej niż ${meta.limit} km/h`,
    hint: (meta) => `Zwolnij przed znakiem — tu wolno jechać najwyżej ${meta.limit} km/h.`,
    ok: 'Dobra prędkość!', fail: 'Za szybko — przekroczona prędkość',
  },
  crossing: {
    icon: '{sign_crossing}', title: 'Przejście dla pieszych — zatrzymaj się, jeśli ktoś czeka',
    hint: 'Zwolnij przy zebrze. Jeśli ktoś czeka na chodniku, zatrzymaj się i przepuść go.',
    ok: 'Ładnie przepuszczone!', fail: 'Nie zatrzymałeś się przed pieszym',
  },
  light: {
    icon: '{sign_lights}', title: 'Na czerwonym stoimy, ruszamy na zielonym',
    hint: 'Popatrz na sygnalizator. Na czerwonym stój przed linią, jedź dopiero na zielonym.',
    ok: 'Dobrze — poczekałeś na zielone!', fail: 'Przejazd na czerwonym świetle',
  },
  solid: {
    icon: '{sign_no_overtake}', title: 'Linia ciągła — nie wyprzedzaj, jedź za autem',
    hint: 'Widzisz białą ciągłą linię na środku drogi? Tu nie wolno wyprzedzać — jedź spokojnie za autem.',
    ok: 'Dobrze, zostałeś za autem!', fail: 'Wyprzedzanie na linii ciągłej',
  },
  overtake: {
    icon: '{arrows_lr}', title: 'Linia przerywana — wyprzedź {arrow_down} i wróć na swój pas {arrow_up}',
    hint: 'Teraz linia jest przerywana — możesz wyprzedzić auto z przodu i wrócić na swój pas.',
    ok: 'Ładne wyprzedzanie!', fail: 'Nie wróciłeś na swój pas po wyprzedzeniu',
  },
  yield: {
    icon: '{sign_yield}', title: 'Ustąp pierwszeństwa autu na drodze głównej',
    hint: 'To droga podporządkowana — zwolnij i popatrz w obie strony, zanim wjedziesz.',
    ok: 'Dobrze ustąpione!', fail: 'Nie ustąpiłeś pierwszeństwa',
  },
  lane: {
    icon: '{sign_lane}', title: 'Jedź swoim pasem (dalszym, gdy jedziesz w lewo)',
    hint: 'Trzymaj się swojego pasa przez całą trasę.',
    ok: null, fail: 'Zjechałeś na cudzy pas',
  },
  oncoming: {
    icon: '{car}', title: 'Auto z naprzeciwka — trzymaj się swojego pasa',
    hint: 'Zaraz minie cię auto z naprzeciwka — nie zjeżdżaj na jego pas.',
    ok: 'Dobrze wyminięte!', fail: 'Zjechałeś na pas auta z naprzeciwka',
  },
  noentry: {
    icon: '{sign_no_entry}', title: 'Zakaz wjazdu w uliczkę — jedziemy prosto',
    hint: 'Ten znak mówi, że w tę uliczkę nie wolno wjeżdżać — jedziemy dalej prosto.',
    ok: null, fail: null,
  },
  park: {
    icon: '{sign_parking}', title: 'Zjedź {arrow_down}{arrow_down} do zatoki i zaparkuj między liniami',
    hint: 'Naciśnij {arrow_down} dwa razy, żeby zjechać do zatoki, wjedź między linie wolnego miejsca i zatrzymaj się całkiem.',
    ok: 'Świetnie zaparkowane!', fail: 'Nie zaparkowałeś w zatoce',
  },
  finish: {
    icon: '{finish}', title: 'Dojedź do końca trasy',
    hint: 'Jedź prosto do końca trasy.',
    ok: 'Dojechałeś!', fail: null,
  },
  crash: { fail: 'Stuknąłeś auto!' },
};

// Short "sign" name + one-line rule for the pre-drive briefing — distinct from TASK_COPY_PL
// because a briefing item can't know a limit sign's actual number (BRIEFING_PL only gets
// the route's segment list, not SEGMENT_FEATURES) and needs a name distinct from its title.
const BRIEFING_ITEM_PL = {
  limit: { name: 'Ograniczenie prędkości', rule: 'Nie przekraczaj prędkości pokazanej na znaku.' },
  stop: { name: 'Znak STOP', rule: 'Zatrzymaj się całkiem przed linią i przepuść auto z boku.' },
  yield: { name: 'Ustąpienie pierwszeństwa', rule: 'Zwolnij i ustąp pierwszeństwa autu na drodze głównej.' },
  crossing: { name: 'Przejście dla pieszych', rule: 'Zatrzymaj się, jeśli ktoś czeka, żeby przejść.' },
  light: { name: 'Sygnalizacja świetlna', rule: 'Na czerwonym stoimy, ruszamy dopiero na zielonym.' },
  solid: { name: 'Linia ciągła', rule: 'Nie wyprzedzaj — jedź za autem z przodu.' },
  noentry: { name: 'Zakaz wjazdu', rule: 'Nie skręcaj w uliczkę z zakazem — jedziemy prosto.' },
  park: { name: 'Parkowanie', rule: 'Zjedź {arrow_down}{arrow_down} do zatoki i zatrzymaj się między liniami.' },
};

// Which briefing kind a segment id stands for — 'country' carries no sign of its own.
const BRIEFING_KIND_BY_SEGMENT = {
  limit50: 'limit', school: 'limit', stop: 'stop', yield: 'yield', crossing: 'crossing',
  lights: 'light', solid: 'solid', noentry: 'noentry', parking: 'park', country: null,
};

const resolve = (value, meta) => (typeof value === 'function' ? value(meta) : value ?? null);

// The dashed-line window that follows a continuous line. 2600 px is the full exercise: the
// car ahead is still rolling at 10 km/h (TrafficActor PARKED_KMH), so at 40 km/h the child
// gains 30 km/h on it and needs ~1.4 s — ~1550 px of road — to get out, past and back, plus
// room to think between the two lane changes. It may be cut short by whatever the route
// announces next (a junction's own announcement and its side-road car must not land in the
// middle of an overtake) — but never below OVERTAKE_MIN_PX, since a window nobody can finish
// an overtake in is worse than no window at all: the segment then simply doesn't get one.
const OVERTAKE_PX = 2600;
const OVERTAKE_CLEAR_PX = 300;   // road left between the end of the window and the next task
const OVERTAKE_MIN_PX = 900;

/**
 * Lay a route's segments out end to end in world px. The car drives LEFT, so the first
 * segment (driven order, index 0) sits at the highest x and each next one continues from
 * where the last left off — `placed[i].x1 === placed[i - 1].x0`.
 */
export function layoutSegments(segments) {
  const worldWidth = segments.length * SEGMENT_WORLD_WIDTH;
  const placed = segments.map((id, index) => {
    const x1 = worldWidth - index * SEGMENT_WORLD_WIDTH;
    const x0 = x1 - SEGMENT_WORLD_WIDTH;
    return { id, index, x0, x1 };
  });
  return { worldWidth, placed };
}

/**
 * Every task a route asks of the driver, sorted by decreasing `x0` — the order they're
 * driven into. `features` is `SEGMENT_FEATURES` (or a Task-6 replacement with the same
 * shape). `mode` is `'lesson'` or `'exam'`; `lesson` (1-3) only matters for `'lesson'`.
 */
export function buildTasks(segments, features, { mode, lesson } = {}) {
  const { worldWidth, placed } = layoutSegments(segments);
  const isLesson1 = mode === 'lesson' && lesson === 1;
  const advanced = mode === 'exam' || (mode === 'lesson' && lesson === 3);
  const hasParking = segments.includes('parking');
  // A segment-local x turned into this laid-out segment's absolute world x — the
  // same transform segmentFeatures.mjs exports as featureWorldX, called here with the
  // segment's own index/count so every callsite below stays a plain (seg, localX).
  const worldX = (seg, localX) => featureWorldX(seg.index, segments.length, localX);
  const tasks = [];
  // Segments whose overtake window is sized at the very end, once every other task's x0 is
  // known (see the loop after this one).
  const overtakeAfter = [];
  // Lesson 3 carries a `country` buffer both before AND after `yield` (the first gives the
  // `overtake` window after `solid` room to breathe; the second is the original spot the
  // oncoming car has always met the driver from). Only ONE may raise `oncoming` — picked here
  // as the LAST `country` segment in the route, i.e. the one nearest `parking`, so the car's
  // position doesn't move for routes that only ever had the one (this keeps lesson 3's
  // oncoming exactly where it always announced). The exam never gets one from `country` at
  // all: its single oncoming car stays the school stretch's (see the `school` case below).
  const lastCountryIndex = segments.reduce(
    (last, id, i) => (id === 'country' ? i : last), -1,
  );

  const push = (segId, index, kind, x0, x1, meta = {}) => {
    const copy = TASK_COPY_PL[kind] ?? {};
    tasks.push({
      id: `${segId}-${index}-${kind}`,
      kind,
      x0, x1,
      severity: SEVERITY_BY_KIND[kind] ?? 'none',
      title: resolve(copy.title, meta) ?? '',
      hint: isLesson1 ? resolve(copy.hint, meta) : null,
      icon: copy.icon ?? null,
      meta,
    });
  };

  for (const seg of placed) {
    const f = features[seg.id] ?? {};
    switch (seg.id) {
      case 'limit50':
      case 'school': {
        const sign = seg.id === 'limit50' ? f.sign50 : f.sign30;
        const limit = seg.id === 'limit50' ? 50 : 30;
        const signX = worldX(seg, sign.x);
        // Announced 400 px before the sign, but only ENFORCED from the sign itself
        // (routeRules judges speed from `meta.signX`) — braking is allowed to finish.
        push(seg.id, seg.index, 'limit', signX + 400, seg.x0, { limit, signX });
        // The exam route has no `country` segment, so without this it would never meet a
        // car coming the other way — the one hazard that punishes a lazy lane. The school
        // stretch is where it goes: a slow limit, no junction of its own.
        if (mode === 'exam' && seg.id === 'school') {
          push(seg.id, seg.index, 'oncoming', seg.x1, seg.x0, { trigger: seg.x1 - 200 });
        }
        break;
      }
      case 'stop': {
        const stopLine = worldX(seg, f.stopLine.x);
        const sideRoad = worldX(seg, f.sideRoad.x);
        push(seg.id, seg.index, 'stop', stopLine + 900, stopLine, {
          // 260 px of bonnet travel to stop in (was 140 — a perfectly sensible brake landed
          // 2 px outside it in the live check that flagged this, see the fix report's #2).
          window: [stopLine + 260, stopLine], sideRoad,
        });
        break;
      }
      case 'yield': {
        const yieldLine = worldX(seg, f.yieldLine.x);
        const sideRoad = worldX(seg, f.sideRoad.x);
        push(seg.id, seg.index, 'yield', yieldLine + 900, yieldLine, {
          window: [yieldLine + 200, yieldLine], sideRoad,
        });
        break;
      }
      case 'crossing': {
        const zebraX0 = worldX(seg, f.zebra.x0);
        const zebraX1 = worldX(seg, f.zebra.x1);
        push(seg.id, seg.index, 'crossing', zebraX0 + 900, zebraX1, { zebra: { x0: zebraX0, x1: zebraX1 } });
        break;
      }
      case 'lights': {
        const lightPole = worldX(seg, f.lightPole.x);
        const stopLine = worldX(seg, f.stopLine.x);
        push(seg.id, seg.index, 'light', lightPole + 900, stopLine, { lightPole });
        break;
      }
      case 'solid': {
        const parkedCar = worldX(seg, f.parkedCar.x);
        push(seg.id, seg.index, 'solid', seg.x1, seg.x0, { parkedCar });
        // The dashed line starts where the continuous one ends — but how much of it the
        // overtake may use depends on what comes next, so it is sized below.
        if (advanced) overtakeAfter.push(seg);
        break;
      }
      case 'noentry': {
        const sideStreet = worldX(seg, f.sideStreet.x);
        push(seg.id, seg.index, 'noentry', sideStreet + 500, sideStreet, { sideStreet });
        break;
      }
      case 'parking': {
        const bays = f.bays.map((b) => ({ x0: worldX(seg, b.x0), x1: worldX(seg, b.x1) }));
        push(seg.id, seg.index, 'park', seg.x1, ROUTE_FINISH_X, { bays });
        break;
      }
      case 'country': {
        if (advanced && mode !== 'exam' && seg.index === lastCountryIndex) {
          push(seg.id, seg.index, 'oncoming', seg.x1, seg.x0, { trigger: seg.x1 - 200 });
        }
        break;
      }
      default:
        break;
    }
  }

  if (!hasParking) {
    const last = placed[placed.length - 1];
    // A 400 px run-up so the strip can announce the finish instead of popping it up
    // on the frame it resolves.
    push(last.id, last.index, 'finish', ROUTE_FINISH_X + 400, ROUTE_FINISH_X, {});
  }

  // One continuous "stay in your lane" task spanning the whole route.
  const first = placed[0];
  push(first.id, first.index, 'lane', worldWidth, 0, {});

  // Now that every other task is placed, size the dashed-line windows. A window that ran
  // over the next task's announcement would tell the child to pull into the near lane
  // exactly where the next junction's car is about to appear — so it stops
  // OVERTAKE_CLEAR_PX short of it, and is dropped entirely if that leaves too little road.
  for (const seg of overtakeAfter) {
    const next = tasks.reduce((best, t) => (t.kind !== 'lane' && t.x0 < seg.x0
      && (best === null || t.x0 > best.x0) ? t : best), null);
    const x1 = Math.max(seg.x0 - OVERTAKE_PX, next ? next.x0 + OVERTAKE_CLEAR_PX : 0, 0);
    if (seg.x0 - x1 < OVERTAKE_MIN_PX) continue;
    push(seg.id, seg.index, 'overtake', seg.x0, x1, {});
  }

  return tasks.sort((a, b) => b.x0 - a.x0);
}

/**
 * The instructor's pre-drive briefing: every distinct sign/rule this route's segments raise,
 * in the order they're driven into. Doesn't need SEGMENT_FEATURES — it's naming the RULE,
 * not measuring where on the road it sits. `mode` ('lesson'|'exam') is part of the
 * interface for parity with buildTasks, though every rule reads the same either way.
 */
export function BRIEFING_PL(segments, mode) {
  const seen = new Set();
  const items = [];
  for (const segId of segments) {
    const kind = BRIEFING_KIND_BY_SEGMENT[segId];
    if (!kind || seen.has(kind)) continue;
    seen.add(kind);
    const { icon } = TASK_COPY_PL[kind];
    const { name, rule } = BRIEFING_ITEM_PL[kind];
    items.push({ icon, name, rule });
  }
  return items;
}

export const EXAM_RULES_PL = 'Bez błędów poważnych, najwyżej 2 drobne.';
